import { getCookie } from "./getCookie";
import { createRefreshApi } from "./createRefreshApi";
import { authStateManager } from "./authStateManager";
import { useAppStore } from "../../appStore";
import { logger } from "../../utils/logger";

// How long a caller waits for a refresh that another caller already started.
// Matches concurrentAPIErrorHandler's REFRESH_FLAG_TIMEOUT.
const WAIT_FOR_INFLIGHT_MS = 15000;
const WAIT_POLL_MS = 250;

/** Statuses/bodies the backend uses for a stale or invalid access token. The
 *  403 bodies mirror universalAPIErrorHandler's token-error detection. */
export const isAuthTokenError = (error: unknown): boolean => {
  const err = error as {
    response?: { status?: number; data?: unknown };
  };
  const status = err?.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  let body = "";
  try {
    const data = err.response?.data;
    body = typeof data === "string" ? data : JSON.stringify(data ?? "");
  } catch {
    body = "";
  }
  return (
    body.includes("Invalid token") ||
    body.includes("Token is expired") ||
    body.includes("Token not found") ||
    body.includes("Returned invalid token from cache") ||
    body.includes("SocialToken matching query does not exist")
  );
};

const waitForInflightRefresh = async (): Promise<boolean> => {
  const deadline = Date.now() + WAIT_FOR_INFLIGHT_MS;
  while (useAppStore.getState().refreshingToken && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, WAIT_POLL_MS));
  }
  return Boolean(getCookie("token"));
};

/**
 * Refresh the access token in place and resolve true on success.
 *
 * Unlike getRefreshToken / getRefreshTokenWhenConcurrentAPICalls this is
 * promise-shaped, never navigates, and never clears auth state — the caller
 * decides what a failed refresh means for its own UI (ENG-1284: a paused live
 * view that outlived the token TTL shows "session expired" instead of being
 * yanked to Cognito). It shares the app store's `refreshingToken` flag with
 * the concurrent handler, so parallel callers coalesce onto one POST.
 */
export const refreshAuthToken = async (): Promise<boolean> => {
  const refreshToken = getCookie("token_secret");
  if (!refreshToken) return false;

  const store = useAppStore.getState();
  if (store.refreshingToken) return waitForInflightRefresh();

  store.setRefreshingToken(true);
  try {
    const response = await createRefreshApi("").post("/auth/refresh/", {
      refresh_token: refreshToken,
    });
    const { token, token_secret: nextRefreshToken } = response.data ?? {};
    if (!token) return false;
    authStateManager.setAuthState(token, nextRefreshToken ?? refreshToken);
    return true;
  } catch (error) {
    logger.log("refreshAuthToken failed", error);
    return false;
  } finally {
    useAppStore.getState().setRefreshingToken(false);
    localStorage.setItem("tokenRefreshing", "false");
  }
};
